/**
 * RealTimePoller.ts
 *
 * Orchestrates interval-based polling of geospatial data sources.
 * Supports multiple named layers with independent intervals, visibility
 * control, and exponential backoff on HTTP 429 responses.
 *
 * Usage:
 *   const poller = new RealTimePoller();
 *   poller.register('earthquakes', fetchUSGSEarthquakes, 60_000, pushFn, 4);
 *   poller.register('fires', fetchFIRMSHotspots, 300_000, pushFn, 4);
 *   poller.setLayerVisible('earthquakes', false); // skip fetches while hidden
 *   poller.unregister('earthquakes');             // stop polling
 */

type PushFn = (layerId: string, data: Float32Array, count: number) => void;
type FetchFn = () => Promise<Float32Array | null>;

/**
 * RealTimePoller manages polling intervals for one or more named layers.
 *
 * - Calls fetchFn at the specified interval
 * - Calls pushFn only when fetchFn returns non-null data
 * - Skips fetch entirely when the layer is not visible
 * - Applies exponential backoff (max 2 doublings → 4× base interval) on HTTP 429
 * - Continues polling if fetchFn throws (logs warning, does not crash)
 */
export class RealTimePoller {
  private intervals = new Map<string, ReturnType<typeof setInterval>>();
  private hiddenLayers = new Set<string>();
  private backoffMultiplier = new Map<string, number>();

  /**
   * Register a layer for periodic polling.
   *
   * @param layerId       - Unique layer identifier
   * @param fetchFn       - Async function returning Float32Array | null
   * @param intervalMs    - Base polling interval in milliseconds
   * @param pushFn        - Called with (layerId, data, count) when fetchFn returns non-null
   * @param strideFloats  - Number of floats per marker (used to compute count)
   */
  register(
    layerId: string,
    fetchFn: FetchFn,
    intervalMs: number,
    pushFn: PushFn,
    strideFloats: number
  ): void {
    // Cancel any existing interval for this layer
    this.unregister(layerId);
    this.backoffMultiplier.set(layerId, 1);

    const tick = async () => {
      // Skip fetch while layer is hidden
      if (this.hiddenLayers.has(layerId)) return;

      try {
        const data = await fetchFn();
        if (data !== null) {
          pushFn(layerId, data, data.length / strideFloats);
          // Reset backoff on success
          this.backoffMultiplier.set(layerId, 1);
        }
      } catch (e: unknown) {
        // On HTTP 429, apply exponential backoff (max 2 doublings → 4× base)
        if (e instanceof Response && e.status === 429) {
          const current = this.backoffMultiplier.get(layerId) ?? 1;
          this.backoffMultiplier.set(layerId, Math.min(current * 2, 4));
        } else if (e instanceof Error && e.name !== 'AbortError') {
          console.warn(`[RealTimePoller] ${layerId} fetch failed:`, e.message);
        }
      }
    };

    // Fire immediately on registration, then at intervals
    tick();

    const id = setInterval(tick, intervalMs);
    this.intervals.set(layerId, id);
  }

  /**
   * Stop polling for the given layer.
   */
  unregister(layerId: string): void {
    const id = this.intervals.get(layerId);
    if (id !== undefined) {
      clearInterval(id);
    }
    this.intervals.delete(layerId);
    this.backoffMultiplier.delete(layerId);
  }

  /**
   * Control whether a layer's fetch is skipped on each interval tick.
   * When visible=false, the interval continues but fetch is not called.
   * When visible=true, polling resumes on the next tick.
   */
  setLayerVisible(layerId: string, visible: boolean): void {
    if (visible) {
      this.hiddenLayers.delete(layerId);
    } else {
      this.hiddenLayers.add(layerId);
    }
  }
}
